import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface UserProfile {
  id: string;
  email: string;
  role: 'ceo' | 'co_ceo' | 'staff' | 'driver' | 'client';
  full_name?: string;
  phone?: string;
}

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, role: string, fullName: string, phone: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchUserProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchUserProfile = async (userId: string) => {
    try {
      // First, try to get user metadata from auth
      const { data: { user: authUser } } = await supabase.auth.getUser();
      
      if (authUser) {
        // Create profile from auth metadata first (fallback)
        const metadataProfile: UserProfile = {
          id: authUser.id,
          email: authUser.email || '',
          role: (authUser.user_metadata?.role || 'client') as UserProfile['role'],
          full_name: authUser.user_metadata?.full_name,
          phone: authUser.user_metadata?.phone,
        };

        // Primary source of truth: users table (role is managed here for staff/admin users)
        try {
          const { data: userData, error: userError } = await supabase
            .from('users')
            .select('id, email, role, full_name, phone')
            .eq('id', userId)
            .maybeSingle();

          if (userData && !userError) {
            // Best-effort: keep profiles table aligned with users table role
            try {
              await supabase.from('profiles').upsert({
                id: userId,
                email: userData.email || authUser.email || '',
                role: userData.role,
                full_name: userData.full_name ?? authUser.user_metadata?.full_name ?? null,
                phone: userData.phone ?? authUser.user_metadata?.phone ?? null,
                created_at: new Date().toISOString(),
              });
            } catch (syncError) {
              console.warn('Could not sync profiles role from users table:', syncError);
            }

            setProfile(userData as UserProfile);
            return;
          }

          // If the users row is not keyed by auth uid, fall back to matching by email (safe: must match auth email)
          if (authUser.email) {
            const { data: userByEmail, error: userByEmailError } = await supabase
              .from('users')
              .select('id, email, role, full_name, phone')
              .eq('email', authUser.email)
              .maybeSingle();

            if (userByEmail && !userByEmailError) {
              // Best-effort: sync profiles to the logged-in auth uid
              try {
                await supabase.from('profiles').upsert({
                  id: userId,
                  email: authUser.email,
                  role: userByEmail.role,
                  full_name: userByEmail.full_name ?? authUser.user_metadata?.full_name ?? null,
                  phone: userByEmail.phone ?? authUser.user_metadata?.phone ?? null,
                  created_at: new Date().toISOString(),
                });
              } catch (syncError) {
                console.warn('Could not sync profiles role from users (email fallback):', syncError);
              }

              setProfile({
                id: userId,
                email: userByEmail.email || authUser.email,
                role: userByEmail.role,
                full_name: userByEmail.full_name,
                phone: userByEmail.phone,
              } as UserProfile);
              return;
            }
          }

          // Secondary source: profiles table
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('id, email, role, full_name, phone')
            .eq('id', userId)
            .maybeSingle();

          if (profileData && !profileError) {
            setProfile(profileData as UserProfile);
            return;
          }

          // Fall back to metadata
          setProfile(metadataProfile);
        } catch (dbError) {
          console.warn('Could not fetch from users/profiles tables, using auth metadata:', dbError);
          setProfile(metadataProfile);
        }
      } else {
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // Last resort: Try to get from auth metadata
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        if (authUser) {
          const basicProfile: UserProfile = {
            id: authUser.id,
            email: authUser.email || '',
            role: (authUser.user_metadata?.role || 'client') as UserProfile['role'],
            full_name: authUser.user_metadata?.full_name,
            phone: authUser.user_metadata?.phone,
          };
          setProfile(basicProfile);
        } else {
          setProfile(null);
        }
      } catch (metaError) {
        console.error('Error fetching from metadata:', metaError);
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      // Profile will be fetched automatically via onAuthStateChange
      // But we'll wait a moment to ensure it's loaded
      if (data.user) {
        await fetchUserProfile(data.user.id);
      }
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const signUp = async (email: string, password: string, role: string, fullName: string, phone: string) => {
    // Create auth user
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          phone: phone,
          role: role,
        }
      }
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('User creation failed');

    // Try to create profile in profiles table, but don't fail if it errors
    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authData.user.id,
          email,
          role,
          full_name: fullName,
          phone,
          created_at: new Date().toISOString(),
        });

      if (profileError) {
        console.warn('Profile creation warning:', profileError);
      }
    } catch (profileError) {
      console.warn('Could not create profile in database:', profileError);
    }

    // If driver role, create driver profile
    if (role === 'driver') {
      try {
        const { error: driverError } = await supabase
          .from('drivers')
          .insert({
            user_id: authData.user.id,
            full_name: fullName,
            phone,
            email,
            verification_status: 'pending',
            availability: false,
            created_at: new Date().toISOString(),
          });

        if (driverError) console.warn('Driver profile creation warning:', driverError);
      } catch (driverError) {
        console.warn('Could not create driver profile:', driverError);
      }
    }

    // If client role, create client profile
    if (role === 'client') {
      try {
        const { error: clientError } = await supabase
          .from('clients')
          .insert({
            user_id: authData.user.id,
            name: fullName,
            phone,
            email,
            type: 'individual',
            created_at: new Date().toISOString(),
          });

        if (clientError) console.warn('Client profile creation warning:', clientError);
      } catch (clientError) {
        console.warn('Could not create client profile:', clientError);
      }
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setUser(null);
    setSession(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`,
    });
    if (error) throw error;
  };

  const value = {
    user,
    profile,
    session,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
